import { useState, useEffect } from "react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { 
  TrendingUp, 
  DollarSign, 
  Clock, 
  Target, 
  ExternalLink, 
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle,
  MinusCircle,
  Shirt
} from "lucide-react";
import { toast } from "sonner";

import { authFetch } from "../context/AuthContext";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const getUrgencyColor = (urgency) => {
  switch (urgency) {
    case "high":
      return "text-red-500 bg-red-50";
    case "medium":
      return "text-amber-500 bg-amber-50";
    case "low":
      return "text-blue-500 bg-blue-50";
    default:
      return "text-green-500 bg-green-50";
  }
};

const getUrgencyIcon = (urgency) => {
  switch (urgency) {
    case "high":
      return <AlertTriangle className="w-4 h-4" />;
    case "medium":
      return <Clock className="w-4 h-4" />;
    case "low":
      return <MinusCircle className="w-4 h-4" />;
    default:
      return <CheckCircle className="w-4 h-4" />;
  }
};

const getScoreColor = (score) => {
  if (score >= 70) return "bg-red-500";
  if (score >= 50) return "bg-amber-500";
  if (score >= 30) return "bg-blue-500";
  return "bg-green-500";
};

export function SmartSell() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchSmartSellData();
  }, []);

  const fetchSmartSellData = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/smart-sell?limit=50`, {
        
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Failed to fetch smart sell data:", error);
      toast.error("Failed to load Smart Sell rankings");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSmartSellData();
    setRefreshing(false);
    toast.success("Rankings refreshed");
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Calculating Smart Sell Scores...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="smart-sell-page">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-primary mb-2">
              Smart Sell
            </h1>
            <p className="text-muted-foreground">
              AI-powered rankings to maximize your wardrobe ROI
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-sm"
            data-testid="refresh-rankings-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border border-border shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                    Total Items
                  </p>
                  <p className="font-heading text-2xl font-medium text-primary">
                    {data?.summary?.total_items || 0}
                  </p>
                </div>
                <Shirt className="w-8 h-8 text-muted-foreground/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-red-200 bg-red-50/30 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest font-semibold text-red-600 mb-2">
                    High Urgency
                  </p>
                  <p className="font-heading text-2xl font-medium text-red-600">
                    {data?.summary?.high_urgency_count || 0}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-300" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-amber-200 bg-amber-50/30 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest font-semibold text-amber-600 mb-2">
                    Medium Urgency
                  </p>
                  <p className="font-heading text-2xl font-medium text-amber-600">
                    {data?.summary?.medium_urgency_count || 0}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-amber-300" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-terracotta-500/30 bg-terracotta-500/5 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest font-semibold text-terracotta-500 mb-2">
                    Potential Value
                  </p>
                  <p className="font-heading text-2xl font-medium text-terracotta-500">
                    ${(data?.summary?.potential_value || 0).toLocaleString()}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-terracotta-500/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How It Works */}
        <Card className="border border-border shadow-none bg-sand-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Target className="w-6 h-6 text-terracotta-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-primary mb-2">How Smart Sell Score Works</h3>
                <p className="text-sm text-muted-foreground">
                  Each item is scored 0-100 based on four factors: <strong>Value Recovery</strong> (market value vs original cost), 
                  <strong> Dormancy</strong> (days unworn), <strong>Opportunity</strong> (absolute market value), and 
                  <strong> Efficiency</strong> (wear frequency vs cost). Higher scores = more urgent to sell.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rankings List */}
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-medium">Rankings</h2>
          
          {data?.rankings?.length > 0 ? (
            <div className="space-y-4">
              {data.rankings.map((item, index) => (
                <Card 
                  key={item.item_id} 
                  className={`border shadow-none overflow-hidden ${
                    item.smart_sell_score.urgency === "high" 
                      ? "border-red-200" 
                      : item.smart_sell_score.urgency === "medium"
                      ? "border-amber-200"
                      : "border-border"
                  }`}
                  data-testid={`smart-sell-item-${item.item_id}`}
                >
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      {/* Rank & Score */}
                      <div className="p-4 md:p-6 md:w-32 flex md:flex-col items-center justify-center gap-3 md:gap-2 bg-sand-50 border-b md:border-b-0 md:border-r border-border">
                        <span className="text-2xl font-heading font-bold text-muted-foreground">
                          #{index + 1}
                        </span>
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${
                            item.smart_sell_score.urgency === "high" ? "text-red-500" :
                            item.smart_sell_score.urgency === "medium" ? "text-amber-500" :
                            item.smart_sell_score.urgency === "low" ? "text-blue-500" :
                            "text-green-500"
                          }`}>
                            {item.smart_sell_score.score}
                          </div>
                          <p className="text-xs text-muted-foreground">Score</p>
                        </div>
                      </div>

                      {/* Item Details */}
                      <div className="flex-1 p-4 md:p-6">
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                          {/* Item Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium ${getUrgencyColor(item.smart_sell_score.urgency)}`}>
                                {getUrgencyIcon(item.smart_sell_score.urgency)}
                                {item.smart_sell_score.urgency.charAt(0).toUpperCase() + item.smart_sell_score.urgency.slice(1)} Urgency
                              </span>
                            </div>
                            <h3 className="font-medium text-primary">
                              {item.brand} {item.item_name}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {item.smart_sell_score.recommendation}
                            </p>

                            {/* Score Breakdown */}
                            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Value Recovery</p>
                                <Progress 
                                  value={item.smart_sell_score.breakdown.value_recovery / 30 * 100} 
                                  className="h-2"
                                />
                                <p className="text-xs font-medium mt-1">{item.smart_sell_score.breakdown.value_recovery}/30</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Dormancy</p>
                                <Progress 
                                  value={item.smart_sell_score.breakdown.dormancy / 30 * 100} 
                                  className="h-2"
                                />
                                <p className="text-xs font-medium mt-1">{item.smart_sell_score.breakdown.dormancy}/30</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Opportunity</p>
                                <Progress 
                                  value={item.smart_sell_score.breakdown.opportunity / 25 * 100} 
                                  className="h-2"
                                />
                                <p className="text-xs font-medium mt-1">{item.smart_sell_score.breakdown.opportunity}/25</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Efficiency</p>
                                <Progress 
                                  value={item.smart_sell_score.breakdown.efficiency / 15 * 100} 
                                  className="h-2"
                                />
                                <p className="text-xs font-medium mt-1">{item.smart_sell_score.breakdown.efficiency}/15</p>
                              </div>
                            </div>
                          </div>

                          {/* Stats & Actions */}
                          <div className="lg:w-64 lg:text-right space-y-4">
                            <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 lg:gap-1">
                              <div>
                                <p className="text-xs text-muted-foreground">Original Cost</p>
                                <p className="font-medium">${item.total_cost}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Market Value</p>
                                <p className="font-medium text-terracotta-500">
                                  approx. ${item.market_value?.toFixed(0) || "N/A"}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Days Owned</p>
                                <p className="font-medium">{item.smart_sell_score.factors.days_owned}</p>
                              </div>
                            </div>

                            {/* Quick List Buttons */}
                            {item.listing_urls && (
                              <div className="flex flex-wrap gap-2 justify-end">
                                <a href={item.listing_urls.grailed || item.listing_urls.grailed_search} target="_blank" rel="noopener noreferrer">
                                  <Button variant="outline" size="sm" className="text-xs h-8">
                                    <ExternalLink className="w-3 h-3 mr-1" />
                                    Grailed
                                  </Button>
                                </a>
                                <a href={item.listing_urls.depop || item.listing_urls.depop_search} target="_blank" rel="noopener noreferrer">
                                  <Button variant="outline" size="sm" className="text-xs h-8">
                                    <ExternalLink className="w-3 h-3 mr-1" />
                                    Depop
                                  </Button>
                                </a>
                                <a href={item.listing_urls.poshmark || item.listing_urls.poshmark_search} target="_blank" rel="noopener noreferrer">
                                  <Button variant="outline" size="sm" className="text-xs h-8">
                                    <ExternalLink className="w-3 h-3 mr-1" />
                                    Poshmark
                                  </Button>
                                </a>
                                <a href={item.listing_urls.therealreal || item.listing_urls.therealreal_search} target="_blank" rel="noopener noreferrer">
                                  <Button variant="outline" size="sm" className="text-xs h-8">
                                    <ExternalLink className="w-3 h-3 mr-1" />
                                    The RealReal
                                  </Button>
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border border-border shadow-none">
              <CardContent className="p-12 text-center">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 text-sand-300" />
                <h3 className="font-heading text-xl text-primary mb-2">No items to rank yet</h3>
                <p className="text-muted-foreground">
                  Add items to your wardrobe to see Smart Sell rankings
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
